import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play } from 'lucide-react';
import { useTVShow, useUpdateTVShow } from '@/hooks/useTVShows';
import { CreateTVShowRequest } from '@/types/model';
import TVShowForm from '@/components/forms/TVShowForm';
import Button from '@/components/ui/Button';
import { useTranslation } from '@/hooks';

const EditTVShow = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data: tvShow, isLoading, isError } = useTVShow(id!);
  const updateTVShowMutation = useUpdateTVShow();

  const handleUpdateTVShow = async (data: CreateTVShowRequest) => {
    if (!id) return;

    try {
      const response = await updateTVShowMutation.mutateAsync({ id, data });
      console.log('Serie actualizada exitosamente:', response);
      navigate(-1);
    } catch (error) {
      console.error('Error updating TV show:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-600">Cargando serie...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !tvShow) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">Error al cargar la serie</div>
              <Button onClick={handleCancel}>
                Volver a Series
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="link"
            onClick={handleCancel}
            className="mb-0"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Series
          </Button>
          <Button
            variant="link"
            onClick={() => navigate(`/content/tv-shows/${id}/episodes`)}
            className="mb-0"
          >
            {t.common.episodes}
            <Play className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <TVShowForm
          initialData={{
            title: tvShow.title,
            spanishTitle: tvShow.spanishTitle,
            originalTitle: tvShow.originalTitle,
            imdbId: tvShow.imdbId,
            tmdbId: tvShow.tmdbId,
            year: tvShow.year,
            firstAirDate: tvShow.firstAirDate,
            lastAirDate: tvShow.lastAirDate,
            totalSeasons: tvShow.totalSeasons,
            totalEpisodes: tvShow.totalEpisodes,
            rating: tvShow.rating,
            actors: tvShow.actors,
            creators: tvShow.creators,
            spanishSynopsis: tvShow.spanishSynopsis,
            englishSynopsis: tvShow.englishSynopsis,
            status: tvShow.status,
            classification: tvShow.classification,
            genres: tvShow.genres,
            categoryIds: tvShow.categoryIds ?? [],
            coverImageUrl: tvShow.coverImageUrl,
            backgroundImageUrl: tvShow.backgroundImageUrl,
            trailerUrl: tvShow.trailerUrl,
            reference: tvShow.reference,
            active: tvShow.active,
            adultContent: tvShow.adultContent,
            isFeatured: tvShow.isFeatured,
            kidsContent: tvShow.kidsContent,
          }}
          onSuccess={handleUpdateTVShow}
          onCancel={handleCancel}
          isEdit={true}
          isSubmitting={updateTVShowMutation.isPending}
        />
      </div>
    </div>
  );
};

export default EditTVShow;
